/**
 * Implements Trasnsactions Saga
 * Main idea is to handle intermediate stages for swap transactions and show their statuses
 */
import { put, fork, delay, call, takeEvery } from 'redux-saga/effects';
import { waitForTransaction } from 'wagmi/actions';
import { Address } from 'viem';
import {
  actionUpdateTransactionStatus,
  ACTION_ADD_TRANSACTION,
} from '../actions/transactions';
import { transactionKlassType } from '../reducers/transactions';
import { getTransactionStatus } from '../components/Trading/helpers/elrondTrading';
import { toIntermediateTransactionInfo } from './../helpers/intermediateTransactions';
import {
  getTonTransaction,
  getTonTxHash,
} from '../components/Trading/helpers/tonTrading';

const getEVMTransactionStatus = async (tx: Address, chainId: number) => {
  try {
    const transaction = await waitForTransaction({
      chainId,
      hash: tx,
    });
    return {
      tx,
      info: toIntermediateTransactionInfo(
        transaction.status === 'success' ? 'success' : 'failed'
      ),
    };
  } catch (e) {
    console.log(e);
    return {
      tx,
      info: toIntermediateTransactionInfo('failed'),
    };
  }
};

const getElrondTransactionStatus = async (tx: Address) => {
  return new Promise((resolve) => {
    const iv = setInterval(async () => {
      try {
        const transaction = await getTransactionStatus(tx);
        const status = transaction.data.status;
        if (status === 'success' || status === 'executed') {
          resolve({
            tx,
            info: toIntermediateTransactionInfo('success'),
          });
          clearInterval(iv);
        }
        if (
          status === 'invalid' ||
          status === 'fail' ||
          status === 'not-executed'
        ) {
          resolve({
            tx,
            info: toIntermediateTransactionInfo('failed'),
          });
          clearInterval(iv);
        }
      } catch (e) {
        console.log(e);
        resolve({
          tx,
          info: toIntermediateTransactionInfo('failed'),
        });
        clearInterval(iv);
      }
    }, 15000);
  });
};

const getTonTransactionStatus = async (msgHash: string) => {
  return new Promise((resolve) => {
    const iv = setInterval(async () => {
      try {
        const tx = await getTonTxHash(msgHash);

        if (tx) {
          const transaction = await getTonTransaction(tx);
          if (transaction.success) {
            resolve({
              tx: msgHash,
              info: toIntermediateTransactionInfo('success'),
            });
            clearInterval(iv);
          }
          if (transaction.aborted || transaction.destroyed) {
            resolve({
              tx: msgHash,
              info: toIntermediateTransactionInfo('failed'),
            });
            clearInterval(iv);
          }
        }
      } catch (e) {
        console.log(e);
        resolve({
          tx: msgHash,
          info: toIntermediateTransactionInfo('failed'),
        });
        clearInterval(iv);
      }
    }, 15000);
  });
};

function* transactionsEventsChannel(
  tx: Address,
  klass: transactionKlassType,
  chainId: number
): any {
  yield put(
    actionUpdateTransactionStatus({
      tx,
      info: toIntermediateTransactionInfo('pending'),
    })
  );

  yield delay(1500);

  let payload;

  if (klass === 'EVM') {
    payload = yield call(getEVMTransactionStatus, tx, chainId);
  } else if (klass === 'Elrond') {
    payload = yield call(getElrondTransactionStatus, tx);
  } else if (klass === 'Ton') {
    payload = yield call(getTonTransactionStatus, tx);
  }
  yield put(actionUpdateTransactionStatus(payload));
}

function* onAddTransaction(action: any): any {
  const { tx, klass, chainId } = action.payload;

  yield fork(transactionsEventsChannel, tx, klass, chainId);
}

export default function* tradesSaga() {
  yield takeEvery(ACTION_ADD_TRANSACTION, onAddTransaction);
}
